// DocumentSearchDropdown — the `<ul role="listbox">` (CTSK-493 §5.4;
// design doc §3.3, §6.6). Owns the visual grouping; the container owns
// the focus index (so the input's aria-activedescendant and this list
// agree on one source of truth).
//
// Positioning follows the project's documented hand-roll pattern
// (UserTypeahead, CTSK-245 — no popover primitive in the deps):
// absolute below the input, input width, ≤60vh with internal scroll.
// Outside-click + Escape handling lives in the container.
//
// ARIA structure (ARIA listbox grammar; axe-clean):
//   div[role=listbox]
//     div[role=group aria-labelledby=hdr]     ← only for nests with ≥2 hits
//       span#hdr (group header, nest name)
//       div[role=option]…
//     div[role=option]                         ← singleton nests + un-nested
// `div`s rather than the design doc's ul/li because ARIA-in-HTML does not
// allow role="group" on <li> (axe `aria-allowed-role`), and the roles
// carry the semantics either way.
//
// Nest-null rows render flat at the end (groupByNest.ts explains why).

import type { DocumentSearchResult as SearchResult } from '@captain-tasko/shared';
import { useEffect, useRef } from 'react';
import { groupByNest } from './groupByNest.js';
import { optionIdFor } from './highlight.js';
import { DocumentSearchResult } from './DocumentSearchResult.js';
import type { LabelKind } from './labelChips.js';

export interface DocumentSearchDropdownProps {
  id: string;
  results: readonly SearchResult[];
  /** Index into `groupByNest(results).flat`; -1 = nothing focused. */
  focusedIndex: number;
  onSelect: (result: SearchResult) => void;
  onFocusIndex: (index: number) => void;
  /** Scope chip / other content rendered above the first result. */
  header?: React.ReactNode;
  /** CTSK-507 — a result's group / collection / folder chip was clicked. */
  onLabelFilter?: (kind: LabelKind, value: string) => void;
}

export function DocumentSearchDropdown({
  id,
  results,
  focusedIndex,
  onSelect,
  onFocusIndex,
  header,
  onLabelFilter,
}: DocumentSearchDropdownProps) {
  const { groups, unnested, flat } = groupByNest(results);
  const focused = focusedIndex >= 0 ? flat[focusedIndex] : undefined;
  const listRef = useRef<HTMLDivElement>(null);

  // Keep the focused option in view while arrowing through a scrolled list.
  useEffect(() => {
    if (!focused || !listRef.current) return;
    // Option ids are globally unique (ULID-based); getElementById avoids
    // a selector round-trip (and jsdom has no CSS.escape).
    const el = document.getElementById(optionIdFor(focused));
    if (el && listRef.current.contains(el)) el.scrollIntoView?.({ block: 'nearest' });
  }, [focused]);

  const indexOf = (r: SearchResult) => flat.indexOf(r);

  function row(r: SearchResult, hideNestBadge = false) {
    return (
      <DocumentSearchResult
        key={r.id}
        result={r}
        focused={focused?.id === r.id}
        onSelect={onSelect}
        onFocusRequest={(x) => onFocusIndex(indexOf(x))}
        hideNestBadge={hideNestBadge}
        onLabelFilter={onLabelFilter}
      />
    );
  }

  return (
    <div className="doc-search__dropdown" data-testid="document-search-dropdown">
      {header}
      <div
        id={id}
        ref={listRef}
        role="listbox"
        aria-label="Document search results"
        className="doc-search__list"
      >
        {groups.map((g) =>
          g.labelled ? (
            <div
              key={g.nest.slug}
              role="group"
              aria-labelledby={`${id}-group-${g.nest.slug}`}
              className="doc-search__group"
            >
              <span id={`${id}-group-${g.nest.slug}`} className="doc-search__group-hdr">
                <span className="doc-search__badge" aria-hidden="true">{g.nest.code}</span>
                {g.nest.name}
              </span>
              {g.items.map((r) => row(r, true))}
            </div>
          ) : (
            g.items.map((r) => row(r))
          ),
        )}
        {unnested.map((r) => row(r))}
      </div>
    </div>
  );
}
